import NiceModal from '@ebay/nice-modal-react'
import { useRouter } from 'next/router'
import React, { useEffect, useRef, useState } from 'react'
import { useAuth } from '@/contexts/auth'
import { MODAL_ID } from '@/constants/modalId'
import withAuth from '@/hoc/withAuth'
import DefaultLayout from '@/layouts/Default'
import {
  ModalOpenPack,
  ModalNewsletterSignUp,
  ModalTwoFANote,
  Loading
} from '@/components'
import InventoryBrowser, { Card } from '@/components/InventoryBrowser'
import { useGetUserAssets } from '@/api/resources/user/assets/get'
import BrowserGridItem, {
  BrowserGridItemType
} from '@/components/InventoryBrowser/BrowserGridItem'
import InventoryFilter from '@/components/InventoryBrowser/filter/InventoryFilter'
import ItemDetail, { DetailAssetType } from '@/components/ItemDetail'
import EmptyUserInventory from '@/components/PageSpecificComponents/inventory/EmptyUserInventory'
import EmptyUserInventoryFilterResult from '@/components/PageSpecificComponents/inventory/EmptyUserInventoryFilterResult'
import NavigationTabs from '@/components/NavigationTabs'
import { HrefLinkDefinition } from '@/types/links'
import useHiddenHeader from '@/hooks/useHiddenHeader'
import useIsSticky from '@/hooks/useIsSticky'
import { classNames } from '@/util/tailwindHelpers'
import { QueryData } from '@/api/resources/catalog/item/listings/get/types'
import WalletContextualBanner from '@/features/wallet/components/WalletContextualBanner'
import useNextQueryParams from '@/hooks/useNextQueryParams'
import {
  AssetSelectionEnum,
  AssetSelectionModeParam
} from '@/components/InventoryBrowser/types'
import InventorySelectionToolbar from '@/components/InventoryBrowser/components/InventorySelectionToolbar'
import { AssetSelectionModes } from '@/components/InventoryBrowser/header/AssetSelectionModes'
import useBreakpoints from '@/hooks/useBreakpoints'
import { ControlsIslandActionsWrapperSmall } from '@/components/ControlsIsland'
import ToggleArcadeValuesButton from '@/components/InventoryBrowser/header/ToggleArcadeValuesButton'
import { useLayoutData } from '@/contexts/layout'
import { SelectionLimitToast } from '@/features/inventory/components/SelectionLimitToast'
import { useArcade } from '@/features/arcade/provider'

type SelectionThreshold = number | null

const seo = { title: 'Inventory', description: '', keywords: '' }

const DEFAULT_QUERY_CONFIG = {
  inventory: 'inventory',
  listings: 'inventory/?show_only_listings=true'
}

const InventoryPageContent = () => {
  const router = useRouter()
  const { query } = router
  const { claimSuccess, addressPurchased } = query

  const { user, notifications } = useAuth()
  const { account_wax_free, account_wax } = user

  const { season } = useArcade()
  const { isMedium } = useBreakpoints(['md'])
  const { selectedAssets } = useLayoutData()
  const { selectionMode } = useNextQueryParams<AssetSelectionModeParam>()
  const queryConfig = useRef(DEFAULT_QUERY_CONFIG)
  const [openUrl, setOpenUrl] = useState()
  const [quantityValue, setQuantityValue] = useState({
    quantity: undefined
  })
  const hasListingsFilter = query.show_only_listings

  const [openSubscriptionModal, setOpenSubscriptionModal] = useState(
    notifications.prompt_subscribe
  )
  const { values_expanded } = query

  const el = useRef<HTMLDivElement>()
  const isSticky = useIsSticky(el, {
    threshold: 0.999,
    rootMargin: '-1px 0px 0px 0px'
  })
  useHiddenHeader(isSticky)

  useEffect(() => {
    if (claimSuccess) {
      NiceModal.show(MODAL_ID.claimSuccess, {
        type: claimSuccess,
        onComplete: () => router.push('/inventory')
      })
    }
  }, [claimSuccess])

  useEffect(() => {
    const alreadyPurchased = account_wax !== account_wax_free
    if (addressPurchased && alreadyPurchased) {
      NiceModal.show(MODAL_ID.wallet.waxPurchaseSuccess, {
        onComplete: () => {
          delete router.query.addressPurchased
          router.push(router)
        }
      })
    }
  }, [query, user])

  // update the queryConfig when the query changes
  useEffect(() => {
    if (hasListingsFilter) {
      queryConfig.current = {
        ...queryConfig.current,
        listings: router.asPath
      }
    } else {
      queryConfig.current = {
        ...queryConfig.current,
        inventory: router.asPath
      }
    }
  }, [queryConfig.current, router.asPath])

  // We are pushing the currently URL with updated params so that if a user tabs back they will maintain their last state
  const INVENTORY_NAV_ITEMS: HrefLinkDefinition[] = [
    {
      label: 'Inventory',
      id: 'inventory',
      path: `${queryConfig.current.inventory}`,
      matchingPath: {
        path: '/inventory'
      }
    },
    {
      label: 'Listings',
      id: 'listings',
      path: `${queryConfig.current.listings}`,
      matchingPath: {
        path: '/inventory'
      },
      matchingParams: new URLSearchParams({ show_only_listings: 'true' })
    }
  ]

  const {
    isFetchingNextPage,
    isLoading: isLoadingAssets,
    isFetching: isFetchingAssets,
    data: assetsData,
    hasNextPage,
    fetchNextPage
  } = useGetUserAssets(query)

  const assetsQueryData = assetsData as unknown as QueryData

  useEffect(() => {
    if (isFetchingAssets || !assetsQueryData) {
      return
    }

    const { filtered_count } = assetsQueryData

    setQuantityValue({
      quantity: filtered_count
    })
  }, [isFetchingAssets])

  if (isLoadingAssets) return <Loading />

  // ASSET SELECTION
  const isSelectionModeActive = !!selectionMode && !isFetchingAssets
  const shouldHideAssetSelectionModes = selectionMode || hasListingsFilter

  let selectionThreshold: SelectionThreshold =
    selectionMode === AssetSelectionEnum.Convert ? season?.convertLimit : null
  const isSelectionThresholdMet =
    selectedAssets?.[selectionMode]?.length === selectionThreshold

  const handleOnDisabledItemAction = () => {
    if (isSelectionThresholdMet) {
      SelectionLimitToast({ threshold: selectionThreshold })
    }
  }

  // @ts-expect-error - this should be resolved when we upgrade to RQ 5 - Josh Dobson - 4/28/23
  const cards: Card<Details>[] = assetsData?.items.map((item, index) => {
    return {
      index: index,
      id: item.id,
      content: (isDetailPanelActive, _, cardMetrics) => (
        <BrowserGridItem
          isSelectionModeActive={isSelectionModeActive}
          isDisabled={isSelectionThresholdMet}
          onDisabledAction={handleOnDisabledItemAction}
          size={cardMetrics.cardLayout}
          key={item.id}
          activeId={item.id}
          asset={item}
          isPublic={false}
          isDetailPanelActive={isDetailPanelActive}
        />
      ),
      details: item
    } as Card<BrowserGridItemType>
  })

  if (assetsQueryData === undefined) return null

  const {
    owners,
    collections,
    total_count,
    inactive_listings_count,
    inventory_value,
    points_available
  } = assetsQueryData

  const assetsValues = {
    inventory_value,
    points_available
  }

  const options = {
    owners,
    collections
  }

  const { type = 'All' } = query
  const title = !query.show_only_listings ? '' : 'Listings / '

  const handleClickValues = () => {
    if (router.query.values_expanded) {
      delete router.query.values_expanded
    } else {
      router.query.values_expanded = 'true'
    }
    router.push(router, null, { scroll: false, shallow: true })
  }

  const isLoadingNewQuery =
    (isLoadingAssets || isFetchingAssets) && !isFetchingNextPage

  return (
    <DefaultLayout
      seo={seo}
      headerConfiguration={{
        offset: { default: 0, md: 0 },
        translucent: false
      }}
      footerConfiguration={{
        removeDefaultOffset: true,
        hasBorder: true
      }}
    >
      {!!total_count && (
        <NavigationTabs
          ref={el}
          className="secondaryNavigationStickyStyles"
          title={`${title}${type}`}
          subtitle="Inventory"
          links={INVENTORY_NAV_ITEMS}
          count={quantityValue.quantity}
        />
      )}
      <div
        className={classNames('flex items-start flex-1', {
          'items-center': !total_count
        })}
      >
        {total_count ? (
          <InventoryBrowser
            isLoading={isLoadingNewQuery}
            options={options}
            assetsValues={assetsValues}
            cards={cards}
            hideSort={!!query.show_duplicates}
            detailAssetType={DetailAssetType.privateAsset}
            hasNextPage={hasNextPage}
            fetchNextPage={fetchNextPage}
            staticElementHeight={72}
            bodyTopSlot={
              <WalletContextualBanner
                className="mb-2 md:mb-3 md:-mt-1"
                inActiveListingsCount={inactive_listings_count}
              />
            }
            renderEmptyResults={
              <EmptyUserInventoryFilterResult query={query} />
            }
            renderFilter={(onToggle, isExpanded) => (
              <InventoryFilter
                assetsValues={assetsValues}
                options={options}
                onToggle={onToggle}
                isExpanded={isExpanded}
                inventoryLoading={isLoadingAssets}
              />
            )}
            searchRightSlot={
              !isMedium && (
                <ToggleArcadeValuesButton
                  isExpanded={!!values_expanded}
                  onClick={handleClickValues}
                />
              )
            }
            renderSelectionToolbar={
              <>
                <ControlsIslandActionsWrapperSmall
                  className={classNames({
                    hidden: shouldHideAssetSelectionModes
                  })}
                >
                  <AssetSelectionModes layout="sm" />
                </ControlsIslandActionsWrapperSmall>

                <InventorySelectionToolbar
                  selectionLimit={selectionThreshold}
                  // @ts-ignore
                  loadedAssets={assetsData?.items}
                />
              </>
            }
            details={(details, presentationStyle) => {
              return (
                <ItemDetail
                  type={DetailAssetType.privateAsset}
                  presentationStyle={presentationStyle}
                  id={details.id.toString()}
                />
              )
            }}
          />
        ) : (
          <EmptyUserInventory account_wax={account_wax} />
        )}
      </div>

      <ModalOpenPack openUrl={openUrl} onClose={() => setOpenUrl(null)} />
      <ModalNewsletterSignUp
        open={openSubscriptionModal}
        onClose={() => setOpenSubscriptionModal(false)}
      />
      <ModalTwoFANote />
    </DefaultLayout>
  )
}

InventoryPageContent.displayName = 'InventoryPage'

const InventoryPage = withAuth(InventoryPageContent)

export default InventoryPage
